import {
  DropIndicator,
  type DropTargetDropEvent,
  type DropTargetOptions,
  type DropTargetTreeInstruction,
  IconButton,
  IconRenderer,
  MenuItem,
  useDraggable,
  useDropTarget,
} from '@affine/component';
import { Guard } from '@affine/core/components/guard';
import { AnimatedSelectionSlot } from '@affine/core/components/selection-checkbox';
import {
  SIDEBAR_ACTION_REVEAL_KEEP_ACTIVE_ATTR,
  SIDEBAR_ACTION_REVEAL_LOCK_EVENT,
  SidebarActionReveal,
} from '@affine/core/modules/app-sidebar/views';
import { ExplorerIconService } from '@affine/core/modules/explorer-icon/services/explorer-icon';
import type { ExplorerType } from '@affine/core/modules/explorer-icon/store/explorer-icon';
import type { DocPermissionActions } from '@affine/core/modules/permissions';
import { WorkbenchLink } from '@affine/core/modules/workbench';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import {
  ArrowDownSmallIcon,
  EditIcon,
  MoreHorizontalIcon,
} from '@blocksuite/icons/rc';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useLiveData, useService } from '@toeverything/infra';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import type { To } from 'history';
import {
  Fragment,
  isValidElement,
  type MouseEvent,
  type RefAttributes,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { StreamLainContextMenu } from '../../../../components/dropdown-panel-chrome/radix-context-menu';
import { StreamLainMenu } from '../../../../components/dropdown-panel-chrome/radix-menu';
import {
  enableSidebarBrowserDragGuard,
  scrubSidebarBrowserDragTransfer,
} from '../../../../modules/app-sidebar/views/sidebar-browser-drag-guard';
import {
  NavigationPanelTreeContext,
  NavigationPanelTreeSelectionContext,
  type NavigationPanelTreeSelectionItem,
  navigationPanelTreeSelectionKey,
} from './context';
import { AnimatedNavigationPanelTreeDisclosureContent } from './disclosure-content';
import { DropEffect } from './drop-effect';
import { NavigationPanelTreeNodeInlineRename } from './inline-rename';
import { NAVIGATION_PANEL_TREE_LEVEL_INDENT } from './layout';
import * as styles from './node.css';
import { resolveNavigationPanelNodeOperations } from './operation-registry';
import {
  getNavigationPanelRevealRequestId,
  hasNavigationPanelTreeNodeRenameIntent,
  NAVIGATION_PANEL_REVEAL_DOC_ID_ATTR,
  NAVIGATION_PANEL_REVEAL_RENAME_ATTR,
  NAVIGATION_PANEL_REVEAL_REQUEST_ID_ATTR,
  NAVIGATION_PANEL_REVEAL_SURFACE_ATTR,
  NavigationPanelRevealRenameCompletionContext,
} from './rename-intent';
import { isSidebarAreaHeaderDropTarget } from './sidebar-area-drag-hover';
import type { NodeOperation } from './types';

export type NavigationPanelTreeNodeDropEffectData = {
  source: { data: AffineDNDData['draggable'] };
  treeInstruction: DropTargetTreeInstruction | null;
};
export type NavigationPanelTreeNodeDropEffect = (
  data: NavigationPanelTreeNodeDropEffectData
) => 'copy' | 'move' | 'link' | undefined;
export type NavigationPanelTreeNodeIcon = React.ComponentType<{
  className?: string;
  draggedOver?: boolean;
  treeInstruction?: DropTargetTreeInstruction | null;
  collapsed?: boolean;
}>;

const SIDEBAR_TREE_EXPAND_REVEAL_DELAY_MS = 190;
const KEEP_ACTION_REVEAL_ACTIVE_PROPS = {
  [SIDEBAR_ACTION_REVEAL_KEEP_ACTIVE_ATTR]: 'true',
} as const;

function findSidebarTreeScrollViewport(element: HTMLElement) {
  return element.closest<HTMLElement>('[data-sidebar-scroll-viewport="true"]');
}

function scrollExpandedSidebarTreeNodeIntoView(root: HTMLElement) {
  const viewport = findSidebarTreeScrollViewport(root);
  const trigger = root.querySelector<HTMLElement>(
    '[data-navigation-panel-tree-node-trigger="true"]'
  );
  if (!viewport || !trigger) {
    return;
  }

  const viewportRect = viewport.getBoundingClientRect();
  const triggerRect = trigger.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();

  if (triggerRect.top < viewportRect.top) {
    viewport.scrollTo({
      top: Math.max(0, viewport.scrollTop + triggerRect.top - viewportRect.top),
      behavior: 'smooth',
    });
    return;
  }

  if (triggerRect.bottom > viewportRect.bottom) {
    viewport.scrollTo({
      top: Math.max(
        0,
        viewport.scrollTop + triggerRect.bottom - viewportRect.bottom
      ),
      behavior: 'smooth',
    });
    return;
  }

  const overflowBottom = rootRect.bottom - viewportRect.bottom;
  if (overflowBottom <= 0) {
    return;
  }

  const triggerTopRoom = Math.max(0, triggerRect.top - viewportRect.top);
  const scrollDelta = Math.min(overflowBottom, triggerTopRoom);
  if (scrollDelta <= 0) {
    return;
  }

  viewport.scrollTo({
    top: Math.max(0, viewport.scrollTop + scrollDelta),
    behavior: 'smooth',
  });
}

function getNodeOperationKey(operation: NodeOperation, scope: string) {
  if (operation.id) {
    return `${scope}:${operation.id}`;
  }
  if (isValidElement(operation.view) && operation.view.key !== null) {
    return `${scope}:${operation.view.key}`;
  }
  return `${scope}:${operation.index}`;
}

function NavigationPanelTreeOperationMenu({
  operations,
}: {
  operations: NodeOperation[];
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dispatchActionLock = useCallback((active: boolean) => {
    triggerRef.current?.dispatchEvent(
      new CustomEvent(SIDEBAR_ACTION_REVEAL_LOCK_EVENT, {
        bubbles: true,
        detail: { active },
      })
    );
  }, []);
  const handleOpenChange = useCallback(
    (open: boolean) => {
      dispatchActionLock(open);
    },
    [dispatchActionLock]
  );

  useEffect(
    () => () => {
      dispatchActionLock(false);
    },
    [dispatchActionLock]
  );

  return (
    <StreamLainMenu
      triggerRef={triggerRef}
      rootOptions={{ onOpenChange: handleOpenChange }}
      contentOptions={{
        side: 'right',
        align: 'center',
      }}
      items={operations.map(operation => (
        <Fragment key={getNodeOperationKey(operation, 'menu')}>
          {operation.view}
        </Fragment>
      ))}
    >
      <IconButton
        {...KEEP_ACTION_REVEAL_ACTIVE_PROPS}
        ref={triggerRef}
        size="16"
        data-testid="navigation-panel-tree-node-operation-button"
      >
        <MoreHorizontalIcon />
      </IconButton>
    </StreamLainMenu>
  );
}

export interface BaseNavigationPanelTreeNodeProps {
  name?: string;
  icon?: NavigationPanelTreeNodeIcon;
  /** sl-jmyl.4: Extra className passed to the Icon component (e.g. glow effect) */
  iconClassName?: string;
  children?: React.ReactNode;
  className?: string;
  active?: boolean;
  extractEmojiAsIcon?: boolean;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  collapsible?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  to?: To;
  postfix?: React.ReactNode;
  operations?: NodeOperation[];
  /** Reveal right-side operations with the shared hover/focus/swipe action rail. */
  revealOperations?: boolean;
  childrenOperations?: NodeOperation[];
  /** Authoritative empty state for callers that synchronously know their children. */
  childrenEmpty?: boolean;
  childrenPlaceholder?: React.ReactNode;

  linkComponent?: React.ComponentType<
    React.PropsWithChildren<{ to: To; className?: string }> &
      RefAttributes<any> & { draggable?: boolean } & {
        onDragStartCapture?: React.DragEventHandler;
      }
  >;
  [key: `data-${string}`]: any;
}

type ExplorerIconConfig = {
  where: ExplorerType;
  id: string;
};
interface WebNavigationPanelTreeNodeProps extends BaseNavigationPanelTreeNodeProps {
  renameable?: boolean;
  onRename?: (newName: string) => void;
  onRenamingChange?: (renaming: boolean) => void;
  renameableGuard?: { docId: string; action: DocPermissionActions };
  defaultRenaming?: boolean;
  renameInputTestId?: string;

  explorerIconConfig?: ExplorerIconConfig | null;

  canDrop?: DropTargetOptions<AffineDNDData>['canDrop'];
  reorderable?: boolean;
  dndData?: AffineDNDData;
  onDrop?: (data: DropTargetDropEvent<AffineDNDData>) => void;
  dropEffect?: NavigationPanelTreeNodeDropEffect;
  selectable?: boolean;
  selectionItem?: NavigationPanelTreeSelectionItem;
}

export const NavigationPanelTreeNode = ({
  children,
  icon: Icon,
  iconClassName,
  name: rawName,
  onClick,
  to,
  active,
  defaultRenaming,
  renameInputTestId,
  renameable,
  renameableGuard,
  onRename,
  onRenamingChange,
  disabled,
  className: externalClassName,
  collapsed,
  setCollapsed,
  collapsible = true,
  canDrop,
  reorderable = true,
  operations = [],
  revealOperations = false,
  postfix,
  childrenOperations = [],
  childrenEmpty,
  childrenPlaceholder,
  linkComponent: LinkComponent = WorkbenchLink,
  dndData,
  explorerIconConfig,
  onDrop,
  dropEffect,
  selectable = true,
  selectionItem,
  extractEmojiAsIcon: _extractEmojiAsIcon,
  ...otherProps
}: WebNavigationPanelTreeNodeProps) => {
  const explorerIconService = useService(ExplorerIconService);
  const t = useI18n();
  const cid = useId();
  const context = useContext(NavigationPanelTreeContext);
  const selectionContext = useContext(NavigationPanelTreeSelectionContext);
  const completeRevealRename = useContext(
    NavigationPanelRevealRenameCompletionContext
  );
  const level = context?.level ?? 0;
  const registerWithParent = context?.registerChild;
  // If no onClick or to is provided, clicking on the node will toggle the collapse state
  const clickForCollapse = !onClick && !to && !disabled;
  const [childCount, setChildCount] = useState(0);
  const [childrenSettled, setChildrenSettled] = useState(!collapsed);
  const childrenAreEmpty = childrenEmpty ?? childCount === 0;
  const [renaming, setRenaming] = useState(defaultRenaming);
  const [protectRenameFocus, setProtectRenameFocus] = useState(false);
  const revealRenameRequestIdRef = useRef<number | null>(null);
  const handleRenamingChange = useCallback(
    (nextRenaming: boolean) => {
      if (!nextRenaming) {
        setProtectRenameFocus(false);
      }
      setRenaming(nextRenaming);
      onRenamingChange?.(nextRenaming);
    },
    [onRenamingChange]
  );
  const startUserRename = useCallback(() => {
    setProtectRenameFocus(false);
    handleRenamingChange(true);
  }, [handleRenamingChange]);
  const startMenuRename = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      setProtectRenameFocus(true);
      handleRenamingChange(true);
    },
    [handleRenamingChange]
  );
  const startRevealRename = useCallback(
    (requestId: number | null) => {
      revealRenameRequestIdRef.current = requestId;
      setProtectRenameFocus(true);
      handleRenamingChange(true);
    },
    [handleRenamingChange]
  );
  const [lastInGroup, setLastInGroup] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const browserSidebarDragGuardCleanupRef = useRef<(() => void) | null>(null);
  const explorerIcon = useLiveData(
    useMemo(
      () =>
        explorerIconConfig
          ? explorerIconService.icon$(
              explorerIconConfig?.where,
              explorerIconConfig?.id
            )
          : null,
      [explorerIconConfig, explorerIconService]
    )
  );
  const selectionKey = selectionItem
    ? navigationPanelTreeSelectionKey(selectionItem)
    : null;
  const canSelect = selectable && !!selectionItem && !disabled;
  const isSelected = selectionKey
    ? (selectionContext?.isSelected(selectionKey) ?? false)
    : false;
  const selectionMode = selectionContext?.selectionMode ?? false;
  const registerSelectionItem = selectionContext?.registerItem;
  const dragSelection =
    canSelect && selectionItem
      ? selectionContext?.getDragSelection(selectionItem)
      : null;

  useLayoutEffect(() => registerWithParent?.(), [registerWithParent]);

  useEffect(() => {
    setChildrenSettled(!collapsed);
  }, [collapsed]);

  useEffect(() => {
    if (defaultRenaming) {
      setProtectRenameFocus(true);
      setRenaming(true);
    }
  }, [defaultRenaming]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !renameable) {
      return;
    }

    const revealSurface = root.closest<HTMLElement>(
      `[${NAVIGATION_PANEL_REVEAL_SURFACE_ATTR}="true"]`
    );
    if (!revealSurface) {
      return;
    }

    const consumePendingRename = () => {
      if (
        hasNavigationPanelTreeNodeRenameIntent(revealSurface, renameInputTestId)
      ) {
        startRevealRename(getNavigationPanelRevealRequestId(revealSurface));
      }
    };
    const renameObserver = new MutationObserver(consumePendingRename);
    renameObserver.observe(revealSurface, {
      attributeFilter: [
        NAVIGATION_PANEL_REVEAL_DOC_ID_ATTR,
        NAVIGATION_PANEL_REVEAL_RENAME_ATTR,
        NAVIGATION_PANEL_REVEAL_REQUEST_ID_ATTR,
      ],
      attributes: true,
    });
    consumePendingRename();
    return () => {
      renameObserver.disconnect();
    };
  }, [renameable, renameInputTestId, startRevealRename]);
  const dragSourceAt = dndData?.draggable?.from?.at;
  const shouldGuardBrowserSidebarDrag =
    typeof dragSourceAt === 'string' &&
    dragSourceAt.startsWith('navigation-panel:');

  const disableBrowserSidebarDragGuard = useCallback(() => {
    browserSidebarDragGuardCleanupRef.current?.();
    browserSidebarDragGuardCleanupRef.current = null;
  }, []);

  const enableBrowserSidebarDragGuard = useCallback(() => {
    if (browserSidebarDragGuardCleanupRef.current) {
      return;
    }

    browserSidebarDragGuardCleanupRef.current = enableSidebarBrowserDragGuard();
  }, []);

  const handleBrowserSidebarDragStartCapture = useCallback(
    (event: React.DragEvent) => {
      if (!shouldGuardBrowserSidebarDrag) {
        return;
      }
      scrubSidebarBrowserDragTransfer(event.dataTransfer);
      enableBrowserSidebarDragGuard();
    },
    [enableBrowserSidebarDragGuard, shouldGuardBrowserSidebarDrag]
  );

  useEffect(() => {
    if (!canSelect || !selectionItem || !registerSelectionItem) {
      return;
    }
    return registerSelectionItem(selectionItem);
  }, [canSelect, registerSelectionItem, selectionItem]);

  const { dragRef, dragging, draggingPosition, dropTarget } = useDraggable<
    AffineDNDData & { draggable: { __cid: string } }
  >(
    () => ({
      data: {
        ...dndData?.draggable,
        ...(dragSelection && dragSelection.length > 1
          ? { selection: { items: dragSelection } }
          : {}),
        __cid: cid,
      },
      disableDragPreview: true,
      toExternalData: () => ({}),
      onDragStart: () => {
        if (shouldGuardBrowserSidebarDrag) {
          enableBrowserSidebarDragGuard();
        }
      },
      onDrop: () => {
        disableBrowserSidebarDragGuard();
      },
    }),
    [
      cid,
      disableBrowserSidebarDragGuard,
      dndData,
      dragSelection,
      enableBrowserSidebarDragGuard,
      shouldGuardBrowserSidebarDrag,
    ]
  );
  const areaHeaderHoverActive = dropTarget.some(target =>
    isSidebarAreaHeaderDropTarget(target.data)
  );

  useEffect(() => {
    if (!dragging || !shouldGuardBrowserSidebarDrag) {
      return;
    }

    enableBrowserSidebarDragGuard();
    return disableBrowserSidebarDragGuard;
  }, [
    disableBrowserSidebarDragGuard,
    dragging,
    enableBrowserSidebarDragGuard,
    shouldGuardBrowserSidebarDrag,
  ]);

  useEffect(
    () => disableBrowserSidebarDragGuard,
    [disableBrowserSidebarDragGuard]
  );

  const handleCanDrop = useMemo<DropTargetOptions<AffineDNDData>['canDrop']>(
    () => args => {
      if (!reorderable && args.treeInstruction?.type !== 'make-child') {
        return false;
      }
      return (typeof canDrop === 'function' ? canDrop(args) : canDrop) ?? true;
    },
    [canDrop, reorderable]
  );

  const {
    dropTargetRef,
    treeInstruction,
    draggedOverDraggable,
    draggedOver,
    draggedOverPosition,
  } = useDropTarget<AffineDNDData & { draggable: { __cid: string } }>(
    () => ({
      data: dndData?.dropTarget,
      treeInstruction: {
        currentLevel: level,
        indentPerLevel: NAVIGATION_PANEL_TREE_LEVEL_INDENT,
        mode: !collapsed
          ? 'expanded'
          : lastInGroup
            ? 'last-in-group'
            : 'standard',
        block:
          reorderable === false
            ? ['reorder-above', 'reorder-below', 'reparent']
            : [],
      },
      onDrop: data => {
        if (
          data.source.data.__cid === cid &&
          data.treeInstruction?.type !== 'reparent'
        ) {
          // Do nothing if dropped on self
          return;
        }
        onDrop?.(data);
        if (
          selectionContext?.selectionMode ||
          data.source.data.selection?.items.length
        ) {
          selectionContext?.clearSelection();
        }
        if (data.treeInstruction?.type === 'make-child' && collapsible) {
          setCollapsed(false);
        }
      },
      canDrop: handleCanDrop,
      allowExternal: true,
    }),
    [
      dndData?.dropTarget,
      level,
      collapsed,
      lastInGroup,
      reorderable,
      handleCanDrop,
      cid,
      onDrop,
      selectionContext,
      collapsible,
      setCollapsed,
    ]
  );
  const isSelfDraggedOver = draggedOverDraggable?.data.__cid === cid;

  useEffect(() => {
    if (
      draggedOver &&
      treeInstruction?.type === 'make-child' &&
      !isSelfDraggedOver
    ) {
      if (!collapsible) return;
      // auto expand when dragged over
      const timeout = setTimeout(() => {
        setCollapsed(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
    return;
  }, [
    collapsible,
    draggedOver,
    isSelfDraggedOver,
    setCollapsed,
    treeInstruction?.type,
  ]);

  useEffect(() => {
    if (rootRef.current) {
      const directParent = rootRef.current.parentElement;
      const wrappedByPresence =
        directParent?.dataset.navigationPanelTreePresenceItem === 'true';
      const parent = wrappedByPresence
        ? directParent?.parentElement
        : directParent;
      const lastChildTarget = wrappedByPresence
        ? directParent
        : rootRef.current;
      if (parent && lastChildTarget) {
        const updateLastInGroup = () => {
          setLastInGroup(parent.lastElementChild === lastChildTarget);
        };
        updateLastInGroup();
        const observer = new MutationObserver(updateLastInGroup);
        observer.observe(parent, {
          childList: true,
        });
        return () => observer.disconnect();
      }
    }
    return;
  }, []);

  const presetOperations = useMemo(
    () =>
      (
        [
          renameable
            ? {
                index: 0,
                view: renameableGuard ? (
                  <Guard
                    permission={renameableGuard.action}
                    docId={renameableGuard.docId}
                  >
                    {can => (
                      <MenuItem
                        key={'navigation-panel-tree-rename'}
                        type={'default'}
                        prefixIcon={<EditIcon />}
                        onClick={startMenuRename}
                        disabled={!can}
                      >
                        {t['com.streamlain.menu.rename']()}
                      </MenuItem>
                    )}
                  </Guard>
                ) : (
                  <MenuItem
                    key={'navigation-panel-tree-rename'}
                    type={'default'}
                    prefixIcon={<EditIcon />}
                    onClick={startMenuRename}
                  >
                    {t['com.streamlain.menu.rename']()}
                  </MenuItem>
                ),
              }
            : null,
        ] as (NodeOperation | null)[]
      ).filter((t): t is NodeOperation => t !== null),
    [renameable, renameableGuard, startMenuRename, t]
  );

  const { contextMenuOperations, menuOperations, inlineOperations } = useMemo(
    () => resolveNavigationPanelNodeOperations(presetOperations, operations),
    [presetOperations, operations]
  );
  const hasOperationControls =
    inlineOperations.length > 0 || menuOperations.length > 0;
  const operationControls = (
    <div
      className={styles.operationControls}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {inlineOperations.map(operation => (
        <Fragment key={getNodeOperationKey(operation, 'inline')}>
          {operation.view}
        </Fragment>
      ))}
      {menuOperations.length > 0 ? (
        <NavigationPanelTreeOperationMenu operations={menuOperations} />
      ) : null}
    </div>
  );

  const contextValue = useMemo(() => {
    return {
      operations: childrenOperations,
      level: (context?.level ?? 0) + 1,
      registerChild: () => {
        setChildCount(c => c + 1);
        return () => setChildCount(c => c - 1);
      },
    };
  }, [childrenOperations, context?.level]);

  const scheduleExpandedNodeReveal = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollExpandedSidebarTreeNodeIntoView(root);
      });
    });
    window.setTimeout(() => {
      scrollExpandedSidebarTreeNodeIntoView(root);
    }, SIDEBAR_TREE_EXPAND_REVEAL_DELAY_MS);
  }, []);

  useEffect(() => {
    if (!defaultRenaming) return;
    const root = rootRef.current;
    if (!root) return;

    const reveal = () => {
      const viewport = findSidebarTreeScrollViewport(root);
      const trigger = root.querySelector<HTMLElement>(
        '[data-navigation-panel-tree-node-trigger="true"]'
      );
      if (!viewport || !trigger) return;
      const viewportRect = viewport.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const maxScrollTop = Math.max(
        0,
        viewport.scrollHeight - viewport.clientHeight
      );
      viewport.scrollTo({
        top: Math.min(
          maxScrollTop,
          Math.max(0, viewport.scrollTop + triggerRect.top - viewportRect.top)
        ),
        behavior: 'smooth',
      });
    };
    let firstFrame: number | null = null;
    let secondFrame: number | null = null;
    firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(reveal);
    });
    const timeout = window.setTimeout(
      reveal,
      SIDEBAR_TREE_EXPAND_REVEAL_DELAY_MS
    );
    return () => {
      if (firstFrame !== null) cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) cancelAnimationFrame(secondFrame);
      window.clearTimeout(timeout);
    };
  }, [defaultRenaming]);

  const setCollapsedWithReveal = useCallback(
    (nextCollapsed: boolean) => {
      setCollapsed(nextCollapsed);
      if (!nextCollapsed) {
        scheduleExpandedNodeReveal();
      }
    },
    [scheduleExpandedNodeReveal, setCollapsed]
  );

  const handleCollapsedChange = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault(); // for links
      if (!collapsible) return;
      setCollapsedWithReveal(!collapsed);
    },
    [collapsed, collapsible, setCollapsedWithReveal]
  );

  const handleRename = useCallback(
    (newName: string) => {
      onRename?.(newName);
    },
    [onRename]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.defaultPrevented) {
        return;
      }
      if (canSelect && selectionContext && selectionItem) {
        const additive = e.metaKey || e.ctrlKey;
        const range = e.shiftKey;
        if (additive || range) {
          e.preventDefault();
          e.stopPropagation();
          selectionContext.toggleSelection(selectionItem, {
            additive: additive || range,
            range,
          });
          return;
        }
        if (selectionMode) {
          e.preventDefault();
          e.stopPropagation();
          selectionContext.toggleSelection(selectionItem, {
            additive: true,
          });
          return;
        }
      }
      if (!clickForCollapse) {
        onClick?.();
      } else if (collapsible) {
        setCollapsedWithReveal(!collapsed);
      }
    },
    [
      canSelect,
      clickForCollapse,
      collapsed,
      collapsible,
      onClick,
      selectionContext,
      selectionItem,
      selectionMode,
      setCollapsedWithReveal,
    ]
  );

  const fallbackIcon = Icon && (
    <Icon
      className={iconClassName}
      draggedOver={draggedOver && !isSelfDraggedOver}
      treeInstruction={treeInstruction}
      collapsed={collapsed}
    />
  );
  const activeDropEffect =
    draggedOver && dropEffect && !isSelfDraggedOver && draggedOverDraggable
      ? dropEffect({
          source: draggedOverDraggable,
          treeInstruction: treeInstruction,
        })
      : undefined;
  const isChildDropTarget =
    draggedOver &&
    treeInstruction?.type === 'make-child' &&
    !isSelfDraggedOver &&
    (dropEffect ? activeDropEffect !== undefined : true);

  const rowContent = (
    <>
      {canSelect ? (
        <AnimatedSelectionSlot
          checked={isSelected}
          visible={selectionMode || isSelected}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            if (!selectionItem || !selectionContext) {
              return;
            }
            selectionContext.toggleSelection(selectionItem, {
              additive: true,
              range: e.shiftKey,
            });
          }}
          checkboxProps={{ style: { pointerEvents: 'none' } }}
        />
      ) : null}
      <div className={styles.toggleIcon}>
        <div
          data-disabled={disabled}
          onClick={handleCollapsedChange}
          data-testid="navigation-panel-collapsed-button"
          className={styles.collapsedIconContainer}
        >
          <ArrowDownSmallIcon
            className={styles.collapsedIcon}
            data-collapsed={collapsed !== false}
          />
        </div>
        <div className={styles.iconContainer}>
          <IconRenderer data={explorerIcon?.icon} fallback={fallbackIcon} />
        </div>
      </div>

      <div className={styles.itemMain}>
        <div
          className={styles.itemContent}
          data-navigation-panel-tree-node-label="true"
          onDoubleClick={event => {
            if (!renameable || disabled) return;
            event.preventDefault();
            event.stopPropagation();
            startUserRename();
          }}
        >
          {renameable && renaming ? (
            <NavigationPanelTreeNodeInlineRename
              setRenaming={handleRenamingChange}
              handleRename={handleRename}
              rawName={rawName}
              protectInitialFocus={protectRenameFocus}
              inputTestId={renameInputTestId}
              onFinish={reason => {
                const requestId = revealRenameRequestIdRef.current;
                revealRenameRequestIdRef.current = null;
                if (requestId !== null) {
                  completeRevealRename(requestId, reason);
                }
              }}
            />
          ) : (
            rawName
          )}
        </div>
        {revealOperations && postfix ? (
          <div className={styles.revealPostfix} data-sidebar-action-reveal-fade>
            {postfix}
          </div>
        ) : (
          postfix
        )}
        {!revealOperations && hasOperationControls ? (
          <div
            className={styles.postfix}
            onClick={e => {
              // prevent jump to page
              e.preventDefault();
            }}
          >
            {operationControls}
          </div>
        ) : null}
      </div>
    </>
  );
  const rowProps = {
    onClick: handleClick,
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        event.target !== event.currentTarget ||
        disabled ||
        (event.key !== 'Enter' && event.key !== ' ')
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.click();
    },
    role: to ? undefined : ('button' as const),
    tabIndex: to || disabled ? undefined : 0,
    'aria-disabled': disabled || undefined,
    'data-active': active,
    'data-disabled': disabled,
    'data-collapsible': collapsible,
    'data-selected': isSelected,
    'data-navigation-tree-select-key': selectionKey ?? undefined,
    'data-child-drop-target': isChildDropTarget,
  };
  const content = revealOperations ? (
    <SidebarActionReveal
      {...rowProps}
      className={clsx(styles.itemRoot, styles.revealItemRoot)}
      contentClassName={styles.revealContent}
      actions={hasOperationControls ? operationControls : undefined}
    >
      {rowContent}
    </SidebarActionReveal>
  ) : (
    <div {...rowProps} className={styles.itemRoot}>
      {rowContent}
    </div>
  );

  return (
    <Collapsible.Root
      open={!collapsed}
      onOpenChange={open => setCollapsedWithReveal(!open)}
      style={assignInlineVars({
        [styles.levelIndent]: `${level * 20}px`,
      })}
      ref={rootRef}
      {...otherProps}
    >
      <StreamLainContextMenu
        asChild
        items={contextMenuOperations.map(operation => (
          <Fragment key={getNodeOperationKey(operation, 'context')}>
            {operation.view}
          </Fragment>
        ))}
      >
        <div
          className={clsx(
            styles.contentContainer,
            styles.draggedOverEffect,
            externalClassName
          )}
          data-navigation-panel-tree-node-trigger="true"
          data-open={!collapsed}
          data-self-dragged-over={isSelfDraggedOver}
          ref={dropTargetRef}
        >
          {to ? (
            <LinkComponent
              to={to}
              className={styles.linkItemRoot}
              ref={dragRef}
              draggable={false}
              onDragStartCapture={handleBrowserSidebarDragStartCapture}
            >
              {content}
            </LinkComponent>
          ) : (
            <div ref={dragRef}>{content}</div>
          )}
          {dragging && !draggingPosition.outWindow
            ? createPortal(
                <div
                  className={styles.liveDraggingPortal}
                  data-area-hover-expand={areaHeaderHoverActive}
                  data-testid="navigation-panel-live-drag-preview"
                  style={{
                    transform: `translate3d(${draggingPosition.clientX + 8}px, ${draggingPosition.clientY + 4}px, 0)`,
                  }}
                >
                  <div className={styles.draggingContainer}>{content}</div>
                </div>,
                document.body
              )
            : null}
          {treeInstruction &&
            // Do not show drop indicator for self dragged over
            !(treeInstruction.type !== 'reparent' && isSelfDraggedOver) &&
            treeInstruction.type !== 'instruction-blocked' &&
            treeInstruction.type !== 'make-child' && (
              <DropIndicator instruction={treeInstruction} />
            )}
          {activeDropEffect && draggedOverPosition && (
            <DropEffect
              dropEffect={activeDropEffect}
              position={draggedOverPosition}
            />
          )}
        </div>
      </StreamLainContextMenu>
      <NavigationPanelTreeContext.Provider value={contextValue}>
        <AnimatedNavigationPanelTreeDisclosureContent
          collapsed={collapsed}
          placeholder={
            childrenSettled && childrenAreEmpty && !collapsed ? (
              <div className={styles.collapseContentPlaceholder}>
                {childrenPlaceholder}
              </div>
            ) : null
          }
          style={{ display: dragging ? 'none' : undefined }}
        >
          {children}
        </AnimatedNavigationPanelTreeDisclosureContent>
      </NavigationPanelTreeContext.Provider>
    </Collapsible.Root>
  );
};
